import RegulationRevisionDates from "@/components/atoms/RegulationRevisionDates";
import { MainTemplate } from "@/components/templates";
import { fmtServiceCode, ServiceCode } from "@/libs/constants";
import { css } from "@emotion/react";
import { color } from "@/styles/theme";
import { useIsSpMode } from "@/hooks";

type Props = {
  bankName: string;
  bankNickname: string;
  bankServiceCodes: string[] | ServiceCode[];
  regulationDate: string;
  revisionDate?: string;
};

// 融資ポータル利用金融向け指定サービス特約( 口座保有条件、課金タイプは無関係に適用 )
export const TermsOfServiceUseLoanPortalPage = ({
  bankName,
  bankNickname,
  bankServiceCodes,
  regulationDate,
  revisionDate,
}: Props) => {
  const isSpMode = useIsSpMode();

  const serviceCodesWithoutLoanPortal = bankServiceCodes.filter(
    (code) => code != ServiceCode.Loan
  );
  const bankNamesServiceNamesWithoutLoanPortal =
    serviceCodesWithoutLoanPortal.map(
      (code) => `${bankName} ${fmtServiceCode(code)}`
    );
  const serviceNames = bankServiceCodes.map((code) => fmtServiceCode(code));

  return (
    <MainTemplate term={true} title={"指定サービスに関する特約"}>
      <h2 css={!isSpMode ? title : spModeTitle}>指定サービスに関する特約</h2>
      <div css={!isSpMode ? container : spModeContainer}>
        <div css={content}>
          <p>
            マネーフォワードエックス株式会社（以下「当社」といいます。）が発行するMikatano
            IDの認証機能を利用する各種サービスのうち当社が指定するサービス（第1条に定義するものをいい、以下「指定サービス」といいます。）をご利用する場合には、「指定サービスに関する特約」（以下「本特約」といいます。）が適用されます。本特約については、利用希望者（指定サービスを利用するに際し、当社と指定サービスの利用に関する契約を締結することを希望する法人、団体又は個人事業主をいいます。以下同じです。）が利用登録した時点で同意したものとさせていただきますので、ご利用の前に必ずお読みください。
            なお、本特約が
            <a href="/terms/id" target="_blank" css={a}>
              Mikatano ID利用規約
            </a>
            及び指定サービスの利用にあたり利用者に適用される利用規約と抵触する場合には、本特約が優先するものとし、本特約の中で使用される用語については、本特約中に別段の定めのある場合を除き、指定サービス利用規約の中で使用される定義、Mikatano
            ID利用規約で使用される定義の順で適用されるものとします。
          </p>
          <h2 css={subTitle}>第１条（指定サービス）</h2>
          <p>
            Mikatano
            ID利用規約及び本特約が適用される指定サービスは、次の各号に定めるとおりとします。
          </p>
          <ol css={olParentheses}>
            {serviceNames.map((serviceName, index) => (
              <li key={index}>
                {bankNickname} {serviceName}
              </li>
            ))}
          </ol>
          <h2 css={subTitle}>第２条（利用条件）</h2>
          <ol css={ol}>
            <li>
              契約者は、
              {bankNamesServiceNamesWithoutLoanPortal.slice(0, -1).join("、") +
                "または" +
                bankNamesServiceNamesWithoutLoanPortal.slice(-1)}
              を利用する場合、これらの指定サービスの利用条件として、当該指定サービスの利用契約の期間中、
              {bankName}
              との間で、
              {bankName}
              のインターネットバンキングの利用に関する契約（以下「インターネットバンキング利用契約」といいます。）を締結し、維持しなければなりません。
            </li>
            <li>
              契約者は、
              {`${bankNickname} Mikatano 融資ポータル`}
              を利用する場合、当該指定サービスの利用条件として、当該指定サービスの利用契約の期間中、
              {bankName}
              との間で、預金の口座を開設し、維持しなければなりません。
            </li>
            <li>
              当社は、
              {`${bankNickname} Mikatano 資金管理`}
              において、契約者が
              {bankName}
              の口座を登録していない場合、契約者が
              {bankName}
              との間で、
              {bankName}
              のインターネットバンキング利用契約を締結していないとみなすことができるものとします。
            </li>
            <li>
              当社は、契約者が、
              {bankName}
              との間で、
              {bankName}
              のインターネットバンキング利用契約を締結していない（インターネットバンキング利用契約の締結後、解除・解約等により契約が終了した場合も含みます。）と当社が判断した場合（前項に基づいて、
              {bankName}
              のインターネットバンキング利用契約を締結していないとみなすことができる場合も含みます。）、Mikatano
              ID利用規約第15条に定める措置等をとることができるものとします。
            </li>
          </ol>
          <h2 css={subTitle}>第３条（準拠法及び管轄）</h2>
          <p>
            本特約の準拠法は日本法とし、本特約に起因し又は関連する一切の紛争については、東京地方裁判所を第一審の専属的合意管轄裁判所とします。
          </p>
          <h2 css={subTitle}>第４条（協議解決）</h2>
          <p>
            当社、利用者及び利用希望者は、本特約に定めのない事項又は本特約の解釈に疑義が生じた場合には、互いに信義誠実の原則に従って協議の上速やかに解決を図るものとします。
          </p>
          <hr css={partitionLine} />
          <RegulationRevisionDates
            regulationDate={regulationDate}
            revisionDate={revisionDate}
          />
        </div>
      </div>
    </MainTemplate>
  );
};

const title = css({
  margin: "auto",
  width: "703px",
  fontWeight: "bold",
  marginBottom: "20px",
});

const spModeTitle = css({
  margin: "auto",
  fontWeight: "bold",
  marginBottom: "20px",
});

const container = css({
  boxSizing: "border-box",
  margin: "auto",
  alignItems: "flexStart",
  padding: "3px 0px 36px",
  width: "703px",
  border: "1px solid #DADEDF",
  borderRadius: "2px",
});

const spModeContainer = css({
  boxSizing: "border-box",
  margin: "auto",
  alignItems: "flexStart",
  padding: "3px 0px 36px",
  width: "703px",
  border: "1px solid #DADEDF",
  borderRadius: "2px",
});

const content = css({
  margin: "0 auto",
  padding: "14px 24px 0px",
});

const subTitle = css({
  alignItems: "flex-start",
  width: "100%",
  margin: "50px 0 20px 0",
  lineHeight: "21px",
  fontWeight: "bold",
});

const ol = css({
  marginBottom: "8px",
});

const olParentheses = css`
  li {
    list-style-type: none;
    counter-increment: cnt;
    text-indent: -2em;
    margin-bottom: 8px;
    padding-right: 24px;
  }

  li::before {
    content: "（" counter(cnt) "）";
  }
`;

const a = css({
  color: "#1A73E8",
});

const partitionLine = css({
  borderTop: `2px solid ${color.gray300}`,
});
